import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import React, { createContext, useEffect, useState } from 'react';

import { setUserId } from '../services/analyticsService';

// Define the shape of the context
interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
}

// Create the context with a default value
export const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  loading: true,
  error: null,
  setError: () => {
    // This will be overridden by the actual implementation
  },
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getAuth();
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(
      auth,
      user => {
        setCurrentUser(user);
        setLoading(false);

        // Set user ID in Google Analytics when user logs in
        if (user) {
          setUserId(user.uid);
        } else {
          // Clear user ID when user logs out
          setUserId('');
        }
      },
      error => {
        console.error('Auth state change error:', error);
        setError('Authentication error. Please try again.');
        setLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const value = {
    currentUser,
    loading,
    error,
    setError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
